/**
 * HPD-5: HPD charges sent to the property tax bill (server
 * ``services/hpd_charges.py``). Its own line, never in exposure or any total
 * (HPD-HOLD-1, 2026-10-02). HPD cannot tell whether the DOF bill was paid, so
 * the copy never says owed or unpaid.
 */
import React from "react";
import MonoLabel from "@/components/design/MonoLabel";
import { formatUsdCents } from "./casesDocket";
import { formatClockDate } from "./violationRemediation/matter";

export interface HpdCharges {
  label: string;
  source: string;
  link: string | null;
  fetchedAt: string;
  workOrders: {
    number: string;
    description: string;
    sentToDof: string;
    amount: string;
    link: string;
  }[];
  /** One per HPD id and fee type; id and link absent before 2026-10-25. */
  fees: {
    type: string;
    count: number;
    amount: string;
    lastSentToDof: string;
    hpdBuildingId?: string | null;
    link?: string | null;
  }[];
  note: string;
}

export const day = (iso: string): string => formatClockDate(new Date(iso.slice(0, 10)));

// 20-05 Shore: 694855 and 948486 both bill a registration fee; the id shows only then.
const feeId = (data: HpdCharges, f: HpdCharges["fees"][number]): React.ReactNode =>
  new Set(data.fees.map((x) => x.hpdBuildingId)).size > 1 && f.hpdBuildingId ? (
    <>
      ,{" "}
      <a href={f.link ?? undefined} target="_blank" rel="noopener noreferrer" className="underline">
        HPD {f.hpdBuildingId}
      </a>
    </>
  ) : null;

export const HpdChargesSection: React.FC<{ data: HpdCharges }> = ({ data }) => (
  <section className="mt-6" data-testid="signals-pane-hpd-charges" aria-label={data.label}>
    <MonoLabel>{data.label}</MonoLabel>
    <ul className="mt-2 flex flex-col">
      {data.workOrders.map((o) => (
        <li key={o.number} className="py-3 border-b border-ink-line">
          <div className="flex items-baseline justify-between gap-3 text-[13px] text-ink">
            <a
              href={o.link}
              target="_blank"
              rel="noopener noreferrer"
              className="underline font-mono"
              aria-label={`Work order ${o.number} on HPD Online`}
            >
              {o.number}
            </a>
            <span className="font-mono">{formatUsdCents(Number(o.amount))}</span>
          </div>
          <p className="mt-1 text-[12px] text-ink-mute leading-snug">
            Sent to DOF {day(o.sentToDof)}. {o.description}
          </p>
        </li>
      ))}
      {data.fees.map((f) => (
        <li
          key={`${f.hpdBuildingId ?? ""}:${f.type}`}
          className="py-3 border-b border-ink-line flex items-baseline justify-between gap-3 text-[13px] text-ink"
        >
          <span>
            {f.type} ({f.count}){feeId(data, f)}, last sent to DOF {day(f.lastSentToDof)}
          </span>
          <span className="font-mono">{formatUsdCents(Number(f.amount))}</span>
        </li>
      ))}
    </ul>
    <p className="mt-3 text-[12px] text-ink-mute leading-snug">
      HPD does not show whether these were paid on the tax bill.{" "}
      {data.note ? `${data.note} ` : null}
      Source:{" "}
      {data.link ? (
        <a href={data.link} target="_blank" rel="noopener noreferrer" className="underline">
          {data.source}
        </a>
      ) : (
        data.source
      )}
      , checked {day(data.fetchedAt)}.
    </p>
  </section>
);
